import {KeyValueCache} from '@multipass-system';

export type ThemeName = 'day' | 'night';
export type AppearanceMode = 'day' | 'night' | 'system';

const APPEARANCE_KEY = 'mp-appearance';

const isAppearanceMode = (value: unknown): value is AppearanceMode => value === 'day' || value === 'night' || value === 'system';

export class ThemeService {
    readonly #doc: Document;
    readonly #cache: KeyValueCache;
    readonly #mql: MediaQueryList;
    #mode: AppearanceMode;

    constructor(doc: Document, cache: KeyValueCache, mql: MediaQueryList) {
        this.#doc = doc;
        this.#cache = cache;
        this.#mql = mql;
        this.#mode = this.#readMode();
        this.#applyResolvedTheme();
        this.#mql.addEventListener('change', () => {
            if (this.#mode === 'system') {
                this.#applyResolvedTheme();
            }
        });
    }

    public get currentMode(): AppearanceMode {
        return this.#mode;
    }

    public get currentTheme(): ThemeName {
        return this.#resolve();
    }

    public setMode(mode: AppearanceMode): void {
        this.#mode = mode;
        this.#cache.setValue(APPEARANCE_KEY, mode);
        this.#applyResolvedTheme();
    }

    #readMode(): AppearanceMode {
        const stored = this.#cache.getValue<AppearanceMode>(APPEARANCE_KEY);
        return isAppearanceMode(stored) ? stored : 'system';
    }

    #resolve(): ThemeName {
        if (this.#mode === 'system') {
            return this.#mql.matches ? 'night' : 'day';
        }
        return this.#mode;
    }

    #applyResolvedTheme(): void {
        this.#doc.documentElement.dataset.theme = this.#resolve();
    }
}
